import * as React from "react"
import { useTracker } from "@modules/analytics"
import { ThemeCss, Link } from "gatsby-interface"
import ToggleCheckbox from "@modules/ui/components/ToggleCheckbox"
import { useToggleManualHostingDeploys } from "../hooks/useToggleManualHostingDeploys"
import { siteBuildsSettings as text } from "@modules/locales/default.js"
import { GatsbyCloudLogo } from "@modules/brand/assets/GatsbyCloudLogo"
import { FormattedMessage } from "@modules/locales"
import { getSiteDetailsTabBasePath } from "@modules/site/details/utils"
import { SiteDetailsTab } from "@modules/site/details/constants"
import { MachinePricingTier } from "@modules/graphql/types"
import { isEligiblePlanTierFor } from "@modules/organization/shared/utils/isEligiblePlanTierFor"
import { getPathToOrgPricing } from "@modules/organization/shared/utils"

export type EnableAutomaticDeploysProps = {
  siteId: string
  organizationId: string
  manualHostingDeploysEnabled: boolean
  gatsbyHostingOn: boolean
  buildsTier: MachinePricingTier
  hostingTier: MachinePricingTier
}
export function EnableAutomaticDeploys({
  siteId,
  organizationId,
  manualHostingDeploysEnabled,
  gatsbyHostingOn,
  buildsTier,
  hostingTier,
}: EnableAutomaticDeploysProps) {
  const linkToSiteSettings = getSiteDetailsTabBasePath(
    SiteDetailsTab.Settings,
    siteId,
    organizationId
  )

  const linkToPlanUpgrade = getPathToOrgPricing(organizationId)
  const isEligiblePlan = isEligiblePlanTierFor(hostingTier)

  const [
    toggleManualHostingDeploys,
    { errorAlert },
  ] = useToggleManualHostingDeploys(siteId)

  const { trackButtonClicked } = useTracker()

  if (gatsbyHostingOn === undefined || buildsTier === undefined) {
    return null
  }

  const disabledHint = !gatsbyHostingOn
    ? text.messages.automaticDeploysDisabledHosting
    : text.messages.automaticDeploysDisabledPlan

  const disabledHintLink = !gatsbyHostingOn
    ? `${linkToSiteSettings}/hosting`
    : linkToPlanUpgrade

  return (
    <React.Fragment>
      <ToggleCheckbox
        id="enable-automatic-deploys-checkbox"
        label={
          <span css={labelCss}>
            {text.labels.enableAutomaticDeploys}
            <GatsbyCloudLogo aria-hidden="true" />
          </span>
        }
        checked={
          gatsbyHostingOn && isEligiblePlan
            ? !manualHostingDeploysEnabled
            : true
        }
        onChange={() => {
          trackButtonClicked(`update automatic deploys`, {
            uiSource: `Site Settings`,
          })
          toggleManualHostingDeploys()
        }}
        disabled={!gatsbyHostingOn || !isEligiblePlan}
        hint={
          (!gatsbyHostingOn || !isEligiblePlan) && (
            <FormattedMessage<never, "link">
              message={disabledHint}
              tags={{
                link: function(content) {
                  return (
                    <Link to={disabledHintLink} variant="SIMPLE">
                      {content}
                    </Link>
                  )
                },
              }}
            />
          )
        }
      />
      {errorAlert}
    </React.Fragment>
  )
}

/* styles */

const labelCss: ThemeCss = theme => ({
  display: `inline-flex`,
  alignItems: `center`,

  svg: {
    marginLeft: theme.space[3],
    height: `1.25em`,
    width: `auto`,
  },
})
